import { useEffect, useRef, useState } from "react";
import { logger } from "whole-shared";

import { createCleaner } from "../../config";
import { match } from "../globals";
import type { ClientTwistCard } from "../rules";
import type { Choice} from "./HudController";
import { hud } from "./HudController";

const TwistCard = ({ card }: { card?: ClientTwistCard }) => {
  if(card === undefined) return "-";

  return <span onMouseEnter={() => hud.emit("tooltip", { twistCard: card })} onMouseLeave={() => hud.emit("tooltip")}>
    {card.name}
  </span>;
};

export const Hud = () => {
  const [[commander1, commander2], setCommanders] = useState<[string, string]>(["", ""]);
  const [choices, setChoices] = useState<Choice[]>([]);
  const [devWindowVisible, setDevWindowVisible] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [visible, setVisibility] = useState(true);
  const [header, setHeader] = useState("none");
  const [subheader, setSubheader] = useState("none");
  const [description, setDescription] = useState("none");
  const [counter_visible, setCounterVisible] = useState(false);
  const [counter, setCounter] = useState(0);
  const [counter_max, setCounterMax] = useState(0);
  const [phase, setPhase] = useState("");
  const [round, setRound] = useState(0);
  const [turn, setTurn] = useState(0);
  const [twistCard, setTwistCard] = useState<ClientTwistCard | undefined>(undefined);
  const [underdog, setUnderdog] = useState(0);
  const [victoryPoints, setVictoryPoints] = useState([0, 0]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const cleaner = createCleaner();

    hud.on("commanders", cleaner, (commander1, commander2) => setCommanders([commander1, commander2]));
    hud.on("choices", cleaner, newChoices => setChoices(newChoices));
    hud.on("devWindow", cleaner, setDevWindowVisible);
    hud.on("enable", cleaner, setEnabled);
    hud.on("visibility", cleaner, setVisibility);
    hud.on("header", cleaner, setHeader);
    hud.on("subheader", cleaner, setSubheader);
    hud.on("description", cleaner, setDescription);
    hud.on("counter_visible", cleaner, visible => setCounterVisible(visible));
    hud.on("counter", cleaner, setCounter);
    hud.on("counter_max", cleaner, setCounterMax);
    hud.on("phase", cleaner, phase => setPhase(phase));
    hud.on("round", cleaner, round => setRound(round));
    hud.on("turn", cleaner, turn => setTurn(turn));
    hud.on("twistCard", cleaner, card => setTwistCard(card));
    hud.on("underdog", cleaner, underdog => setUnderdog(underdog));
    hud.on("victoryPoints", cleaner, points => setVictoryPoints(points));

    hud.on("reset", cleaner, () => {
      setChoices([]);
      setDescription("");
      setEnabled(true);
    });

    return cleaner.clean;
  }, []);

  if(! visible) return null;

  const showVictoryPoint = (points: number) => {
    const str = points.toString();

    return str.length === 1 ? ` ${str}` : str;
  };

  const handleSave = () => {
    try {
      const savedState = match.saveState();
      const blob = new Blob([savedState], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `match-save-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      logger.info("Match state saved successfully");
    } catch(error) {
      logger.error("Error saving match state:", error);
    }
  };

  const handleLoad = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if(! file) return;

    try {
      const text = await file.text();
      match.loadState(text);
      logger.info("Match state loaded successfully");
      // Reset file input so the same file can be selected again
      if(fileInputRef.current) fileInputRef.current.value = "";
    } catch(error) {
      logger.error("Error loading match state:", error);
      if(fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return <div className="panel" id="hud">
    <div style={{ textAlign: "left" }} hidden={false}>
      <div onMouseEnter={() => hud.emit("armiesShow", true)} onMouseLeave={() => hud.emit("armiesShow", false)}>
        <span style={{ fontFamily: "monospace", whiteSpace: "pre" }}>Commander 1: {showVictoryPoint(victoryPoints[0])} -</span> {commander1}
        <br />
        <span style={{ fontFamily: "monospace", whiteSpace: "pre" }}>Commander 2: {showVictoryPoint(victoryPoints[1])} -</span> {commander2}
        <br />
        <span style={{ fontFamily: "monospace" }}>Underdog:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>{" "}
        {underdog === 0 ? "-" : underdog === 1 ? commander1 : commander2}
        <br />
      </div>
      <span style={{ fontFamily: "monospace" }}>Twist card:</span> <TwistCard card={twistCard} />
      <br />
      <span style={{ fontFamily: "monospace" }}>Round:</span> {round}
      <br />
      <span style={{ fontFamily: "monospace" }}>Turn:&nbsp;</span> {turn}
      <br />
      <span style={{ fontFamily: "monospace" }}>Phase:</span> {phase}
    </div>
    {header}
    <br />
    <br />
    {subheader}
    <br />
    <br />
    {description}
    <br />
    <br />
    <div className="text" id="counter" hidden={! counter_visible}>
      {counter} / {counter_max}
      <br />
    </div>
    <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
      {choices.map(([name, onClick, disabled]) => <button key={name} disabled={disabled || ! enabled} onClick={onClick}>
        {name}
      </button>)}
    </div>
    <div style={{ display: "flex", gap: "10px", marginTop: "40px" }}>
      <button onClick={() => hud.emit("devWindow", ! devWindowVisible)} style={{ padding: "5px 10px" }} type="button">
        {"Dev"}
      </button>
      <button onClick={handleSave} style={{ padding: "5px 10px" }}>
        {"Save Match"}
      </button>
      <button onClick={handleLoad} style={{ padding: "5px 10px" }}>
        {"Load Match"}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        // Fix: React expects event handler to return void, not a promise.
        onChange={e => {
          void handleFileChange(e);
        }}
      />
    </div>
  </div>;
};
